import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { IntlProvider, addLocaleData } from 'react-intl';

import { modelOf } from '../../prop-types';
import { LocationContextPropType } from '../../services/LocationContext';
import LanguageStore from '../../store/LanguageStore';
import TranslationStore from '../../store/TranslationStore';
import { parseLanguageCode } from '../../util/url';
import { validLanguages } from '../languageLocales';
import {
  loadLocaleDataDynamically,
  loadMessagesDynamically,
} from '../localeLoader';

const getLocaleFromUrl = (locationContext) => {
  let pathname = null;

  if (typeof window === 'undefined' || window.isSSR) {
    if (locationContext && locationContext.href) {
      const href = locationContext.href;

      if (href.startsWith('/')) {
        pathname = href;
      } else {
        try {
          const url = new URL(href);
          pathname = url.pathname;
        } catch (error) {
          console.warn('Error parsing URL from locationContext:', error);
        }
      }
    }
  } else if (window.location && window.location.pathname) {
    pathname = window.location.pathname;
  }

  if (pathname) {
    try {
      return parseLanguageCode(pathname);
    } catch (error) {
      console.warn('Error parsing language code from URL:', error);
    }
  }

  return null;
};

const determineInitialLocale = (
  languageStore,
  overrideLocale,
  locationContext
) => {
  if (overrideLocale && validLanguages.includes(overrideLocale)) {
    return overrideLocale;
  }

  const urlLocale = getLocaleFromUrl(locationContext);
  if (urlLocale && validLanguages.includes(urlLocale)) {
    return urlLocale;
  }

  if (
    languageStore.activeLocale &&
    validLanguages.includes(languageStore.activeLocale)
  ) {
    return languageStore.activeLocale;
  }

  return 'en';
};

const loadLocaleResources = (locale) => {
  return Promise.all([
    loadLocaleDataDynamically(locale),
    loadMessagesDynamically(locale),
  ])
    .then(([localeData, messages]) => {
      if (localeData && localeData.default) {
        addLocaleData(localeData.default);
      }
      return messages;
    })
    .catch((error) => {
      console.error(`Failed to load locale resources for ${locale}:`, error);
    });
};

const IntlWrapper = ({
  languageStore,
  translationStore,
  overrideLocale,
  loadableMessages,
  preloadedMessages,
  locationContext,
  children,
}) => {
  const locale = useMemo(
    () =>
      determineInitialLocale(languageStore, overrideLocale, locationContext),
    [languageStore.activeLocale, overrideLocale, locationContext]
  );

  const [translations, setTranslations] = useState({
    [locale]: preloadedMessages?.[locale] || null,
  });

  useEffect(() => {
    const currentTranslations = translations[locale];

    // Skip loading if translations already exist (even if empty)
    // Empty translations indicate preloaded messages were provided
    if (currentTranslations !== null && currentTranslations !== undefined) {
      return;
    }

    loadLocaleResources(locale).then((messages) => {
      setTranslations((prev) => ({
        ...prev,
        [locale]: messages,
      }));
    });
  }, [locale]);

  const appMessages = translations[locale];

  if (appMessages === null) {
    return null;
  }

  let extraMessages;
  if (loadableMessages) {
    extraMessages = loadableMessages[locale];
  }

  const messages = {
    ...appMessages,
    ...translationStore.messages,
    ...extraMessages,
  };

  return (
    <IntlProvider locale={locale} messages={messages} defaultLocale="en">
      {children}
    </IntlProvider>
  );
};

IntlWrapper.displayName = 'IntlWrapper';

IntlWrapper.propTypes = {
  languageStore: modelOf(LanguageStore).isRequired,
  translationStore: modelOf(TranslationStore).isRequired,
  locationContext: LocationContextPropType.isRequired,
  loadableMessages: PropTypes.object,
  preloadedMessages: PropTypes.object,
  overrideLocale: PropTypes.string,
  children: PropTypes.node,
};

export default inject('locationContext')(observer(IntlWrapper));
